/**
 * Unified semantic event tracking.
 *
 * Fires the same conversion event to GA4, Meta Pixel, and Microsoft Clarity
 * (for session-replay filtering) with one function call. StackAdapt uses a
 * separate conversion-pixel mechanism that isn't wired here yet.
 *
 * Why this exists: GA4 reported 0 Key Events across 55,237 raw events for
 * the period of Apr 27 – May 24, 2026. The raw `page_view` and Pixel
 * `PageView` events were firing, but no semantic conversion events existed
 * for the team to elevate to "Key Events" inside GA4's admin.
 *
 * Usage:
 *   import { track } from "@/lib/track";
 *   onClick={() => track("survey_start", { source: "homepage_hero" })}
 *
 * The GA4 `transport_type: "beacon"` flag ensures events survive page
 * unloads (critical for outbound-link clicks to survey.haevn.co, where the
 * browser tears down the JS context immediately after the click).
 */

/**
 * Maps our internal semantic event names to Meta Pixel's standard event
 * vocabulary. Standard events get richer optimization signal inside Meta
 * Ads Manager than `trackCustom` events. Events not in this map fall back
 * to `trackCustom` with their semantic name preserved.
 *
 * @see https://www.facebook.com/business/help/402791146561655
 */
const META_STANDARD_EVENT_MAP = {
  survey_start: "Lead",
  survey_complete: "CompleteRegistration",
  waitlist_submit: "Lead",
  email_capture: "Subscribe",
  apply_submit: "SubmitApplication",
  city_card_click: "ViewContent",
  blog_view: "ViewContent",
  contact_submit: "Contact",
};

/**
 * Fire a conversion / engagement event to every wired analytics platform.
 *
 * @param {string} eventName  Snake-case semantic name (e.g. "survey_start").
 * @param {Object} params     Arbitrary string/number params for segmentation.
 */
export function track(eventName, params = {}) {
  if (typeof window === "undefined") return;

  // ---- GA4 ----
  // transport_type: "beacon" guarantees delivery when the user navigates
  // away (e.g. clicks an outbound survey link).
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, {
        ...params,
        transport_type: "beacon",
      });
    }
  } catch (e) {
    /* swallow */
  }

  // ---- Meta Pixel ----
  try {
    if (typeof window.fbq === "function") {
      const standardName = META_STANDARD_EVENT_MAP[eventName];
      if (standardName) {
        window.fbq("track", standardName, params);
      } else {
        window.fbq("trackCustom", eventName, params);
      }
    }
  } catch (e) {
    /* swallow */
  }

  // ---- Microsoft Clarity (session-replay filter tags) ----
  // Lets the team filter session recordings by event name or any string
  // param (e.g. "show me sessions where city = austin").
  try {
    if (typeof window.clarity === "function") {
      window.clarity("event", eventName);
      Object.entries(params).forEach(([k, v]) => {
        if ((typeof v === "string" || typeof v === "number") && String(v).length < 200) {
          window.clarity("set", k, String(v));
        }
      });
    }
  } catch (e) {
    /* swallow */
  }
}

/**
 * Convenience: track survey-link clicks. Use as an onClick handler on any
 * <a> pointing to survey.haevn.co. The `source` arg names the surface so
 * GA4 reports can attribute conversions to specific CTAs.
 *
 * Also pings the server-side /api/track/event endpoint with the current
 * UTM context so the admin UTM Report can attribute survey-starts to a
 * paid-social campaign without relying on GA4 sampling.
 *
 * Fires the Google Ads conversion (AW-18059485742/ebv2CK6ugZccEK7Et6ND)
 * as part of the same call so off-domain survey starts register in Ads.
 *
 * De-duplicated: calls within 250 ms of each other are coalesced to a
 * single fire. Some CTAs have an explicit onClick that calls this AND
 * are also caught by the global SurveyClickTracker listener in App.js —
 * without dedupe, both would fire. 250 ms is far longer than any
 * realistic synchronous call gap but short enough that two genuine
 * back-to-back user clicks would still both count.
 */
let _lastSurveyFireAt = 0;
export function trackSurveyStart(source) {
  const now = Date.now();
  if (now - _lastSurveyFireAt < 250) return;
  _lastSurveyFireAt = now;
  const params = { source };
  track("survey_start", params);

  // Google Ads conversion fire — option (a): outbound-link click model.
  // The survey lives at survey.haevn.co (off-domain), so we fire the AW
  // conversion at the moment of click rather than on a return / thank-you
  // page. This is Google's officially recommended pattern when the
  // conversion event happens off-site. De-duplication is handled by Google
  // Ads itself (one conversion per click within their attribution window).
  //
  // Conversion ID: AW-18059485742/ebv2CK6ugZccEK7Et6ND ("Survey Completion (1)")
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-18059485742/ebv2CK6ugZccEK7Et6ND",
        value: 1.0,
        currency: "USD",
      });
    }
  } catch {
    /* swallow — never block survey navigation on an analytics failure */
  }

  // Fire-and-forget server-side log for UTM attribution. Sends both
  // the canonical `survey_intent` (per the new funnel taxonomy) AND
  // the legacy `survey_start` so older report queries / dashboards
  // continue to count the click — back-compat is cheap here.
  try {
    // eslint-disable-next-line global-require
    const { getStoredUtm, getVisitorId } = require("./utm");
    const utm = getStoredUtm();
    const body = {
      event_name: "survey_intent",
      visitor_id: getVisitorId(),
      path: typeof window !== "undefined" ? window.location.pathname : "",
      params,
      ...utm,
    };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow */
  }
}
